import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RENTAL_UNITS, fmt } from "@/lib/rentals";
import { ArrowLeft, Home, MapPin, Calendar as CalendarIcon, Loader2, CreditCard, X, Flame, BadgeCheck, Edit3, Trash2, ShieldCheck, Users } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

// Expand a start/end ISO range into an inclusive list of YYYY-MM-DD strings
const daysInRange = (startISO, endISO) => {
  try {
    const s = new Date(startISO); s.setHours(0,0,0,0);
    const e = new Date(endISO); e.setHours(0,0,0,0);
    const out = [];
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      out.push(new Date(d));
    }
    return out;
  } catch { return []; }
};

export default function RentalView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [i, setI] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [err, setErr] = useState("");
  const [boostOpen, setBoostOpen] = useState(false);
  const [tiers, setTiers] = useState({});
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [unit, setUnit] = useState("day");
  const [units, setUnits] = useState(1);
  const [paying, setPaying] = useState(false);
  // Availability + owner bookings
  const [avail, setAvail] = useState([]);         // [{start, end, state}] — public
  const [ownerBookings, setOwnerBookings] = useState(null);  // owner-only

  const load = async () => {
    try { const { data } = await api.get(`/rentals/${id}`); setI(data);
      const av = Object.keys(data.available_rates || {});
      if (av.length && !data.available_rates[unit]) setUnit(av[0]);
    } catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => {
    api.get("/promotions/tiers").then(r => setTiers(r.data || {})).catch(()=>{});
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("boost") === "done" && sp.get("session_id")) {
      api.get(`/rentals/boost/status/${sp.get("session_id")}`).finally(load);
    }
  }, []); // eslint-disable-line

  // Load public availability whenever the rental id changes
  useEffect(() => {
    if (!id) return;
    api.get(`/rentals/${id}/availability`)
      .then(r => setAvail(r.data?.ranges || []))
      .catch(() => setAvail([]));
  }, [id]);

  // If this viewer owns the listing, also load the full booking list
  useEffect(() => {
    if (!i?.is_owner) { setOwnerBookings(null); return; }
    api.get(`/rentals/${id}/bookings`)
      .then(r => setOwnerBookings(r.data?.bookings || []))
      .catch(() => setOwnerBookings([]));
  }, [i?.is_owner, id]);

  // Flatten every booked day into a single Set for O(1) calendar disabling
  const bookedDays = useMemo(() => {
    const s = new Set();
    (avail || []).forEach(r => {
      daysInRange(r.start, r.end).forEach(d => {
        s.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
      });
    });
    return s;
  }, [avail]);
  const bookedDateObjs = useMemo(() =>
    Array.from(bookedDays).map(k => {
      const [y, m, d] = k.split("-").map(Number);
      return new Date(y, m - 1, d);
    }),
  [bookedDays]);
  const isDateBooked = (d) => {
    const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    return bookedDays.has(k);
  };

  const rate = i?.available_rates?.[unit];
  const subtotal = useMemo(() => rate ? (Number(rate) * Number(units || 0)) : 0, [rate, units]);
  const deposit = Number(i?.security_deposit || 0);
  const total = subtotal + deposit;
  const mapQuery = useMemo(() => ((i?.address || i?.location) || "").trim(), [i]);
  const showMap = i && mapQuery && ["Real Estate", "Spaces"].includes(i.category);

  const book = async () => {
    if (!user) { nav("/login"); return; }
    if (!rate) { setErr(`This listing has no ${unit} rate.`); return; }
    if (units < (i.min_units || 1)) { setErr(`Minimum ${i.min_units} ${unit}(s)`); return; }
    setPaying(true); setErr("");
    try {
      const { data } = await api.post(`/rentals/${id}/checkout`,
        { start, unit, units: Number(units), origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); }
    finally { setPaying(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this rental listing?")) return;
    try { await api.delete(`/rentals/${id}`); nav("/rentals/mine"); }
    catch (e) { setErr(formatErr(e)); }
  };

  if (err) return <div className="max-w-3xl mx-auto p-10"><p className="text-sm text-[#B84B4B]">{err}</p></div>;
  if (!i) return <div className="max-w-3xl mx-auto p-10 text-[#8A8A8A]">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10" data-testid="rental-view">
      <Link to="/rentals" className="text-sm text-[#4A4A4A] inline-flex items-center gap-1 mb-5 hover:text-[#0A0A0A]"><ArrowLeft size={14}/> Back to rentals</Link>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        <div>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-[#F2F2F0]">
            {i.images?.[imgIdx] ? <img src={i.images[imgIdx]} alt="" data-lightbox data-lightbox-group={`rental-${i.id}`} className="w-full h-full object-cover cursor-zoom-in"/> :
              <div className="w-full h-full flex items-center justify-center text-[#8A8A8A]"><Home size={40}/></div>}
          </div>
          {i.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {i.images.map((src, k) => (
                <button key={k} onClick={()=>setImgIdx(k)}
                  className={`aspect-square rounded-xl overflow-hidden border-2 ${k===imgIdx?"border-[#0A0A0A]":"border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        <aside>
          <p className="overline">{i.category}</p>
          <div className="flex items-center flex-wrap gap-2 mt-1">
            <h1 className="h-display text-3xl flex-1" data-testid="rental-title">{i.title}</h1>
            {i.is_boosted && <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0057FF] text-white inline-flex items-center gap-1"><Flame size={10}/> {i.boost_tier}</span>}
          </div>
          <p className="text-sm text-[#4A4A4A] mt-1 inline-flex items-center gap-1"><MapPin size={12}/> {i.location || "—"}</p>

          <div className="card p-5 mt-5">
            <Link to={`/u/${i.owner_id}`} className="flex items-center gap-3 hover:opacity-80">
              <img src={i.owner?.avatar} alt="" className="h-10 w-10 rounded-full object-cover bg-[#E5E5E3]"/>
              <div className="flex-1 min-w-0">
                <p className="font-medium flex items-center gap-1">{i.owner?.name}
                  {i.owner?.verified && <BadgeCheck size={13} className="text-[#0A0A0A]"/>}
                </p>
                <p className="text-xs text-[#8A8A8A]">{i.owner?.location}</p>
              </div>
            </Link>
          </div>

          {i.is_owner ? (
            <div className="card p-4 mt-3 flex flex-wrap gap-2" data-testid="rental-owner-controls">
              <p className="text-sm text-[#4A4A4A] flex-1">Your rental · {i.status}</p>
              <button onClick={()=>setBoostOpen(true)} className="btn-secondary !py-2 !px-3 text-sm" data-testid="rental-boost-open"><Flame size={13}/> Boost</button>
              <Link to={`/rentals/new?edit=${i.id}`} className="btn-ghost text-sm" data-testid="rental-edit"><Edit3 size={13}/> Edit</Link>
              <button onClick={remove} className="btn-ghost text-sm text-[#B84B4B]" data-testid="rental-delete"><Trash2 size={13}/> Delete</button>
            </div>
          ) : (
            <div className="card p-5 mt-3 space-y-3" data-testid="rental-book-card">
              <div className="grid grid-cols-2 gap-2">
                <div className="block">
                  <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Start</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button"
                        className="input pl-9 mt-1.5 relative w-full text-left cursor-pointer hover:border-[#0A0A0A] transition-colors"
                        data-testid="rental-start">
                        <CalendarIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] pointer-events-none"/>
                        {start
                          ? new Date(start + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                          : <span className="text-[#8A8A8A]">Pick a date</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start" data-testid="rental-start-popover">
                      <Calendar
                        mode="single"
                        selected={start ? new Date(start + "T00:00:00") : undefined}
                        onSelect={(d) => {
                          if (d) {
                            const yyyy = d.getFullYear();
                            const mm = String(d.getMonth() + 1).padStart(2, "0");
                            const dd = String(d.getDate()).padStart(2, "0");
                            setStart(`${yyyy}-${mm}-${dd}`);
                          }
                        }}
                        disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || isDateBooked(d)}
                        modifiers={{ booked: bookedDateObjs }}
                        modifiersClassNames={{ booked: "line-through text-[#B84B4B] opacity-70" }}
                        initialFocus
                      />
                      <p className="text-[11px] text-[#8A8A8A] px-3 pb-2">
                        <span className="inline-block h-2 w-2 rounded-full bg-[#B84B4B]/60 mr-1 align-middle"/>
                        Struck-through dates are already booked.
                      </p>
                    </PopoverContent>
                  </Popover>
                </div>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Period</span>
                  <select className="input mt-1.5" value={unit} onChange={(e)=>setUnit(e.target.value)} data-testid="rental-unit">
                    {RENTAL_UNITS.filter(u => i.available_rates?.[u.id] != null).map(u =>
                      <option key={u.id} value={u.id}>{u.long}</option>
                    )}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">How many {unit}{units === 1 ? "" : "s"}?</span>
                <input type="number" min={i.min_units || 1} value={units} onChange={(e)=>setUnits(Number(e.target.value || 1))}
                  className="input mt-1.5" data-testid="rental-units"/>
              </label>

              <div className="border-t border-[#E5E5E3] pt-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[#4A4A4A]">{units} × {fmt(rate, i.currency)}{unit==='hour'?'/hr':unit==='day'?'/day':unit==='week'?'/wk':'/mo'}</span><span>{fmt(subtotal, i.currency)}</span></div>
                {deposit > 0 && (
                  <div className="flex justify-between"><span className="text-[#4A4A4A] inline-flex items-center gap-1"><ShieldCheck size={11}/> Security deposit (refundable)</span><span>{fmt(deposit, i.currency)}</span></div>
                )}
                <div className="flex justify-between font-medium pt-1"><span>Total</span><span data-testid="rental-total">{fmt(total, i.currency)}</span></div>
              </div>
              <button onClick={book} disabled={paying} className="btn-primary w-full" data-testid="rental-book">
                {paying ? <><Loader2 size={14} className="animate-spin"/> Opening Stripe…</> : <><CreditCard size={14}/> Book now</>}
              </button>
              <p className="text-[11px] text-[#8A8A8A] text-center inline-flex items-center justify-center gap-1 w-full">
                <ShieldCheck size={11}/> Paid securely through Hoomanz · 2 % Hoomanz Tip added on top
              </p>
              {err && <p className="text-sm text-[#B84B4B]" data-testid="rental-book-error">{err}</p>}
            </div>
          )}
        </aside>
      </div>

      <div className="card p-6 mt-8 max-w-3xl">
        <p className="overline mb-2">About this rental</p>
        <p className="whitespace-pre-wrap text-[#0A0A0A]" data-testid="rental-description">{i.description}</p>
      </div>

      {/* Availability calendar — visible to everyone. Owners get an extra booked-list. */}
      <div className="card p-6 mt-6 max-w-3xl" data-testid="rental-availability-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <p className="overline">Availability</p>
            <p className="text-sm text-[#4A4A4A] mt-1">
              {bookedDays.size === 0
                ? "Fully available — no bookings yet."
                : `${bookedDays.size} booked day${bookedDays.size === 1 ? "" : "s"} · struck-through dates aren't available.`}
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[#8A8A8A]">
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-[#0A0A0A]"/> Today</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-[#B84B4B]/60"/> Booked</span>
          </div>
        </div>
        <div className="rounded-2xl border border-[#E5E5E3] p-2 inline-block bg-white" data-testid="rental-availability-calendar">
          <Calendar
            mode="single"
            selected={undefined}
            disabled={(d) => true}
            modifiers={{ booked: bookedDateObjs }}
            modifiersClassNames={{ booked: "line-through text-[#B84B4B]" }}
            numberOfMonths={window.matchMedia?.("(min-width: 768px)")?.matches ? 2 : 1}
          />
        </div>

        {/* Owner-only detailed list */}
        {i.is_owner && ownerBookings !== null && (
          <div className="mt-5 pt-5 border-t border-[#E5E5E3]" data-testid="rental-owner-bookings">
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-[#0057FF]"/>
              <p className="text-sm font-medium">Bookings on this listing</p>
            </div>
            {ownerBookings.length === 0 ? (
              <p className="text-sm text-[#8A8A8A]">Nobody has booked yet.</p>
            ) : (
              <ul className="space-y-2">
                {ownerBookings.map(b => (
                  <li key={b.id} className="flex items-center gap-3 text-sm p-3 rounded-xl bg-[#FAFAF8] border border-[#E5E5E3]"
                      data-testid={`rental-booking-row-${b.id}`}>
                    <img src={b.renter?.avatar} alt="" className="h-8 w-8 rounded-full object-cover bg-[#E5E5E3]"/>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{b.renter?.name || "Renter"}</p>
                      <p className="text-xs text-[#4A4A4A]">
                        {new Date(b.start).toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" })}
                        {" → "}
                        {new Date(b.end).toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" })}
                        {" · "}{b.units} {b.unit}{b.units === 1 ? "" : "s"}
                      </p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full
                      ${b.payment_status === "paid" ? "bg-[#0A0A0A] text-white"
                        : b.status === "cancelled" ? "bg-[#FBE8E5] text-[#B84B4B]"
                        : "bg-[#F2F2F0] text-[#4A4A4A]"}`}>
                      {b.payment_status === "paid" ? "paid" : b.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {showMap && (
        <div className="card overflow-hidden mt-6 max-w-3xl" data-testid="rental-map">
          <div className="px-6 pt-5 pb-3 flex items-center justify-between">
            <div>
              <p className="overline">Location</p>
              <p className="text-sm text-[#4A4A4A] inline-flex items-center gap-1 mt-1"><MapPin size={12}/> {i.address || i.location}</p>
            </div>
            <a className="text-xs underline text-[#4A4A4A] hover:text-[#0A0A0A]"
               href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
               target="_blank" rel="noreferrer" data-testid="rental-map-open">Open in Google Maps</a>
          </div>
          <iframe
            title="Map"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=14&output=embed`}
            width="100%" height="360"
            style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}

      {boostOpen && (
        <BoostModal listing={i} tiers={tiers} onClose={()=>setBoostOpen(false)}/>
      )}
    </div>
  );
}

function BoostModal({ listing, tiers, onClose }) {
  const [picking, setPicking] = useState(null);
  const [err, setErr] = useState("");
  const buy = async (tier) => {
    setPicking(tier);
    try { const { data } = await api.post(`/rentals/${listing.id}/boost/checkout`, { tier, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); setPicking(null); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="rental-boost-modal">
      <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl p-7 max-w-lg w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl">Boost this rental</h3>
          <button onClick={onClose} className="text-[#8A8A8A] hover:text-[#0A0A0A]"><X size={16}/></button>
        </div>
        <div className="space-y-2">
          {Object.entries(tiers).map(([id, t]) => (
            <button key={id} onClick={()=>buy(id)} disabled={!!picking}
              data-testid={`rental-boost-tier-${id}`}
              className="w-full card p-4 text-left hover:border-[#0A0A0A] transition-colors">
              <div className="flex items-center gap-3">
                <Flame size={16} className="text-[#0057FF]"/>
                <div className="flex-1">
                  <p className="font-medium">{t.label}</p>
                  <p className="text-xs text-[#4A4A4A]">{t.blurb}</p>
                </div>
                <p className="font-medium">${t.price}</p>
                {picking === id && <Loader2 size={14} className="animate-spin"/>}
              </div>
            </button>
          ))}
        </div>
        {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}
      </div>
    </div>
  );
}
